import React, { useState } from 'react';
import {
  Box, Typography, Button, Stack, TextField,
  InputAdornment, IconButton,
} from '@mui/material';
import { Plus, Trash2, Pencil } from 'lucide-react';
import ZmanSelect from '../components/ZmanSelect';
import { getZmanLabel } from '../components/zmanDefinitions';

const navy = '#1e3a5f';

const inputSx = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  fontSize: '0.82rem',
};

const EMPTY_TIME = () => ({ label: '', type: 'fixed', time: '18:00', baseTime: 'sunset', offset: 0 });

const timeSummary = (t) => {
  if (t.type === 'fixed') return t.time;
  return `${getZmanLabel(t.baseTime)}${t.offset !== 0 ? ` ${t.offset > 0 ? '+' : ''}${t.offset} דק'` : ''}`;
};

/**
 * CustomTimesStep
 * הוספת זמנים נוספים לציר הזמנים (ZmanimWidget)
 *
 * Props:
 *   formData  { customTimes: [] }
 *   onChange  (partial) => void
 */
const CustomTimesStep = ({ formData, onChange, todayZmanim }) => {
  const times = formData?.customTimes || [];
  const [draft, setDraft] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const isOpen = draft !== null;

  const updateTimes = (updated) => onChange({ customTimes: updated });
  const removeTime = (index) => {
    updateTimes(times.filter((_, i) => i !== index));
    if (editingIndex === index) closeDraft();
  };

  const openAdd = () => {
    setEditingIndex(null);
    setDraft(EMPTY_TIME());
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setDraft({ ...times[index] });
  };

  const closeDraft = () => {
    setDraft(null);
    setEditingIndex(null);
  };

  const saveDraft = () => {
    if (!draft.label.trim()) return;
    if (editingIndex !== null) {
      updateTimes(times.map((t, i) => i === editingIndex ? { ...draft } : t));
    } else {
      updateTimes([...times, { ...draft }]);
    }
    closeDraft();
  };

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        זמנים נוספים
      </Typography>

      {/* הסבר הקשר — מה מגיע אוטומטית ומה השלב הזה מיועד לו */}
      <Box sx={{
        border: '1px solid #bfdbfe',
        borderRadius: 2,
        bgcolor: '#eff6ff',
        p: 1.75,
        mb: 1.5,
      }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1d4ed8', mb: 0.75 }}>
          💡 בדרך כלל — תפילות מגיעות אוטומטית מהרכיבים האחרים
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: '#1e40af', lineHeight: 1.65 }}>
          זמני תפילות יום חול, שבת ומועד <strong>מוזרמים לציר אוטומטית</strong> מרכיביהם (תפילות יום חול, זמני שבת, זמני מועד). אין צורך להגדיר אותם כאן — הם מופיעים בציר ברמת פירוט מלאה הכוללת עקיפות, חזרות ועוד.
        </Typography>
      </Box>

      {/* שימושים מיועדים לשלב זה */}
      <Box sx={{
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        bgcolor: '#fafafa',
        p: 1.75,
        mb: 2,
      }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.75 }}>
          השלב הזה מיועד ל:
        </Typography>
        <Stack spacing={0.6}>
          <Typography sx={{ fontSize: '0.77rem', color: '#4b5563', lineHeight: 1.55 }}>
            • <strong>תפילה מיוחדת חד-פעמית</strong> שלא מופיעה באף רכיב אחר (סיום, ערב חג, תפילת גשם...)
          </Typography>
          <Typography sx={{ fontSize: '0.77rem', color: '#4b5563', lineHeight: 1.55 }}>
            • <strong>אירוע נקודתי</strong> שרוצים לסמן על הציר (אסיפת קהל, הרצאה, כינוס...)
          </Typography>
          <Typography sx={{ fontSize: '0.77rem', color: '#6b7280', lineHeight: 1.55 }}>
            • <strong>חלופה אם אין רכיב תפילות נפרד</strong> — ניתן להוסיף כאן זמני תפילה ידנית, אך שים לב: רמת ההגדרה כאן מצומצמת בהרבה ממה שמאפשר רכיב תפילות יום חול.
          </Typography>
        </Stack>
      </Box>

      {/* אזהרה — תמידיות */}
      <Box sx={{
        border: '1px solid #fcd34d',
        borderRadius: 2,
        bgcolor: '#fffbeb',
        p: 1.75,
        mb: 2,
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
      }}>
        <Typography sx={{ fontSize: '1rem', lineHeight: 1, mt: '1px', flexShrink: 0 }}>⚠️</Typography>
        <Box>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#92400e', mb: 0.5 }}>
            זמן שנוסף כאן יופיע כל יום — עד שיוסר ידנית
          </Typography>
          <Typography sx={{ fontSize: '0.77rem', color: '#78350f', lineHeight: 1.6 }}>
            בניגוד לתפילות שמגיעות מרכיבים אחרים ומנוהלות לפי ימים ועקיפות — זמן שתוסיף כאן יוצג <strong>בכל יום ראשון עד שישי</strong> (עד חצות) ללא הפסקה, עד שתחזור ותמחק אותו ידנית. אל תוסיף כאן אירועים חד-פעמיים ותשכח — הם יישארו בציר לנצח.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
        {/* Right side (RTL): list */}
        <Box sx={{ flex: isOpen ? '0 0 40%' : 1, minWidth: 0, transition: 'flex 0.2s' }}>
          <Button
            startIcon={<Plus size={15} />}
            onClick={openAdd}
            disabled={isOpen}
            sx={{
              border: '1.5px dashed #d1d5db',
              borderRadius: '10px', color: '#6b7280', textTransform: 'none',
              fontSize: '0.8rem', px: 2, py: 0.75, width: '100%', mb: 1.5,
              '&:hover': { borderColor: navy, color: navy, bgcolor: '#f0f4fa' },
              '&.Mui-disabled': { borderColor: '#e5e7eb', color: '#c0c0c0' },
            }}
          >
            הוסף זמן
          </Button>

          {times.length > 0 ? (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflowY: 'auto', maxHeight: 'calc(70vh - 160px)' }}>
              {times.map((t, i) => {
                const isActive = editingIndex === i;
                return (
                  <Box key={i} sx={{
                    px: 1.5, py: 1,
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    bgcolor: isActive ? '#eff6ff' : i % 2 === 0 ? '#fafafa' : 'white',
                    borderBottom: i < times.length - 1 ? '1px solid #f0f0f0' : 'none',
                    borderRight: isActive ? `3px solid ${navy}` : '3px solid transparent',
                    transition: 'all 0.15s',
                  }}>
                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#111827' }}>
                        {t.label}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: '0.72rem', color: '#6b7280' }}>
                        {timeSummary(t)}
                      </Typography>
                    </Stack>
                    <IconButton size="small" onClick={() => startEdit(i)}
                      sx={{ color: isActive ? navy : '#9ca3af', '&:hover': { color: navy }, p: 0.5 }}>
                      <Pencil size={13} />
                    </IconButton>
                    <IconButton size="small" onClick={() => removeTime(i)}
                      sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444' }, p: 0.5 }}>
                      <Trash2 size={13} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          ) : !isOpen && (
            <Typography sx={{ textAlign: 'center', fontSize: '0.78rem', color: '#9ca3af', mt: 1 }}>
              אין זמנים נוספים — ניתן לדלג על שלב זה
            </Typography>
          )}
        </Box>

        {/* Left side (RTL): form */}
        {isOpen && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box key={editingIndex ?? 'new'} sx={{ border: '1px solid #bae6fd', borderRadius: 2, bgcolor: '#f0f9ff', p: 2, mb: 1.5 }}>
              <Stack spacing={1.5}>
                {/* Label */}
                <TextField
                  size="small"
                  label="תווית"
                  placeholder="למשל: מנחה גדולה, חצות הלילה..."
                  value={draft.label}
                  onChange={(e) => setDraft(d => ({ ...d, label: e.target.value }))}
                  fullWidth
                  sx={{ ...inputSx, bgcolor: 'white' }}
                />

                {/* Type toggle */}
                <Stack direction="row" spacing={1}>
                  {[{ val: 'fixed', lbl: 'שעה קבועה' }, { val: 'relative', lbl: 'יחסי לזמן הלכתי' }].map(opt => (
                    <Box
                      key={opt.val}
                      onClick={() => setDraft(d => ({ ...d, type: opt.val }))}
                      sx={{
                        px: 2, py: 0.75, borderRadius: '20px', cursor: 'pointer', fontSize: '0.8rem',
                        fontWeight: draft.type === opt.val ? 700 : 400,
                        border: `1.5px solid ${draft.type === opt.val ? navy : '#e5e7eb'}`,
                        bgcolor: draft.type === opt.val ? '#f0f4fa' : 'white',
                        color: draft.type === opt.val ? navy : '#6b7280',
                        transition: 'all 0.15s',
                      }}
                    >
                      {opt.lbl}
                    </Box>
                  ))}
                </Stack>

                {/* Fixed: time picker */}
                {draft.type === 'fixed' && (
                  <TextField
                    size="small"
                    label="שעה"
                    type="time"
                    value={draft.time}
                    onChange={(e) => setDraft(d => ({ ...d, time: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    sx={{ ...inputSx, maxWidth: 160, bgcolor: 'white' }}
                  />
                )}

                {/* Relative: base zman + offset */}
                {draft.type === 'relative' && (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <ZmanSelect
                      value={draft.baseTime}
                      onChange={(key) => setDraft(d => ({ ...d, baseTime: key }))}
                      keyPrefix=""
                      zmanimForDay={todayZmanim}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="offset"
                      value={draft.offset}
                      onChange={(e) => setDraft(d => ({ ...d, offset: Number(e.target.value) }))}
                      inputProps={{ style: { textAlign: 'center', fontSize: '0.82rem' }, min: -120, max: 120 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>דק׳</Typography></InputAdornment>,
                      }}
                      sx={{ width: 90, flexShrink: 0, ...inputSx, bgcolor: 'white' }}
                    />
                  </Stack>
                )}

                {/* Buttons */}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button onClick={closeDraft} sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}>
                    ביטול
                  </Button>
                  <Button
                    variant="contained"
                    onClick={saveDraft}
                    disabled={!draft.label.trim()}
                    sx={{
                      bgcolor: navy, color: 'white', textTransform: 'none', fontSize: '0.8rem',
                      boxShadow: 'none', borderRadius: '8px',
                      '&:hover': { bgcolor: '#152844', boxShadow: 'none' },
                    }}
                  >
                    {editingIndex !== null ? 'עדכן' : 'הוסף'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CustomTimesStep;
